import { FrameManager } from "../../Components/FramePlayer.js";
import { useState, useCallback, useMemo } from "react";
import { useQueue } from "../../Context/QueueProvider.tsx";
import { usePlayerRef } from "../../Context/PlayerRefProvider.tsx";
import { IpodVolume } from "./IpodVolume.js";
import { useBrowserName } from "../../Hooks/useBrowserName.tsx";
import { colors } from "../../styles/theme.js";
import { IpodWarning } from "./IpodWarning.js";
import { useSystemStatus } from "../../Context/SystemStatusProvider.tsx";

const ipodColors = {
  black: colors.black,
  white: colors.white,
  background: colors.accent,
};

const formatSecondsToMinutesAndSeconds = (seconds) => {
  seconds = Math.floor(seconds); // Ensure no decimals
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const IpodContent = ({ stationName }) => {
  const {
    totalSongs,
    playingIndex,
    currentSongDuration,
    currentArtistName,
    currentSongName,
    debugData,
  } = useQueue();

  const { status, statusMessage, statusHeader } = useSystemStatus();

  const { playerRef } = usePlayerRef();

  const trimmedSongInfo = useMemo(() => {
    return {
      songName:
        currentSongName.length > 20
          ? currentSongName.substring(0, 20) + "..."
          : currentSongName,
      artistName:
        currentArtistName.length > 20
          ? currentArtistName.substring(0, 20) + "..."
          : currentArtistName,
    };
  }, [currentSongName, currentArtistName]);

  // Keep playingTime synced with the actual current time of the player
  const [playingTime, setPlayingTime] = useState(
    playerRef.current?.getCurrentTime ? playerRef.current.getCurrentTime() : 0
  );

  // Updates playingTime every 200ms for a "live" UI, using requestAnimationFrame for smooth syncing
  useState(() => {
    let rafId;
    function update() {
      if (playerRef.current?.getCurrentTime) {
        setPlayingTime(playerRef.current.getCurrentTime());
      }
      rafId = requestAnimationFrame(update);
    }
    rafId = requestAnimationFrame(update);
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [playerRef]);

  if (status === "DOWN" || status === "MAINTENANCE") {
    return <IpodWarning header={statusHeader} message={statusMessage} />;
  }

  return (
    <>
      <div
        style={{
          flexDirection: "row",
          fontWeight: "bold",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `3px solid black`,
          paddingTop: 5,
          paddingBottom: 5,
        }}
      >
        <i className="i pixelarticons:play"></i>
        <span>{stationName}</span>
        <i
          className="i pixelarticons:battery-1"
          style={{ paddingRight: 5 }}
        ></i>
      </div>

      <div
        style={{
          flexGrow: 1,
          width: "100%",
          alignSelf: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            alignSelf: "flex-start",
            paddingTop: 5,
            paddingLeft: 5,
            fontSize: 40,
            width: "95%",
            textAlign: "left",
          }}
        >{`${playingIndex + 1} of ${totalSongs}`}</div>
        <div style={{ fontSize: 20 }}>{JSON.stringify(debugData)}</div>
        <div
          style={{
            textAlign: "center",
            width: "100%",
            flexGrow: 1,
            alignItems: "center",
            justifyContent: "space-between",
            display: "flex",
            flexDirection: "column",
            paddingTop: 20,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "bold",
              lineHeight: 1.4,
              flexGrow: 1,
            }}
          >
            {currentSongName && <span>{trimmedSongInfo.songName}</span>}
            {currentArtistName && <span>{trimmedSongInfo.artistName}</span>}
          </div>

          <div style={{ width: "90%", paddingBottom: 30 }}>
            <div
              style={{
                border: `3px solid black`,
                minWidth: "100%",
                height: "20px",
                borderRadius: "20px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${(playingTime / currentSongDuration) * 100}%`,
                  height: "100%",
                  borderRadius: "20px 0 0 20px",
                  backgroundColor: "black",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                width: "100%",
                fontSize: 40,
                paddingBottom: 10,
              }}
            >
              <span>{formatSecondsToMinutesAndSeconds(playingTime)}</span>
              <span>
                {formatSecondsToMinutesAndSeconds(currentSongDuration)}
              </span>
            </div>
            <IpodVolume />
          </div>
        </div>
      </div>
    </>
  );
};

const IpodScreen = ({ stationName }) => {
  const { isLoading } = usePlayerRef();
  const { isLoading: loadingSystemStatus } = useSystemStatus();

  return (
    <div
      className="radial-border-box"
      style={{
        fontFamily: "Chicago Kare",
        backgroundColor: ipodColors.background,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        flexGrow: 1,
        fontSize: 45,
        height: 500,
        width: 500,
      }}
    >
      {isLoading || loadingSystemStatus ? (
        <div
          style={{
            height: "100%",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Loading...
        </div>
      ) : (
        <IpodContent stationName={stationName} />
      )}
    </div>
  );
};

export function IpodLayout({ firstLoad }) {
  const { videoData, durationMap, durationInSeconds, stationName } = useQueue();

  const browserName = useBrowserName();
  const { playerRef } = usePlayerRef();
  // Add support for volume url param (parse and set volume on playerRef)

  const forcePlay = useCallback(() => {
    if (!playerRef?.current) return;
    playerRef?.current?.playVideo();
  }, [playerRef]);

  // if (browserName !== "SecondLife") {
  //   return (
  //     <FrameManager
  //       firstLoad={firstLoad}
  //       videoData={videoData}
  //       durationMap={durationMap}
  //       durationInSeconds={durationInSeconds}
  //       muted={true}
  //     />
  //   );
  // }

  return (
    <>
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
        }}
      >
        {
          <FrameManager
            firstLoad={firstLoad}
            videoData={videoData}
            durationMap={durationMap}
            durationInSeconds={durationInSeconds}
            style={{
              width: "200px",
              height: "200px",
            }}
          />
        }
      </div>
      <button onClick={forcePlay}>force play</button>
      <div
        style={{
          width: 500,
          position: "absolute",
          border: `10px solid black`,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        }}
      >
        <IpodScreen stationName={stationName} />
      </div>
    </>
  );
}
